import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { Text, TextProps } from '../../typography';
import DevelopmentContent, { DevelopmentSection } from './DevelopmentContent.pn';
import { DevUnorderdList } from './DevelopmentContent.style.pn';

interface DrumfordMeadowContentProps {}

const DrumfordMeadowContent: React.FC<DrumfordMeadowContentProps> = () => {
  const theme = useTheme();

  const textProps = useMemo(() => {
    const p: TextProps = {
      fontSize: 17,
      lineHeight: 25,
      mt: 8,
      mb: 16,
      color: theme.white,
    };
    return p;
  }, [theme]);

  return (
    <div data-testid="drumford-meadow-content">
      <DevelopmentContent bg="#7778b5">
        <DevelopmentSection narrow>
          <iframe
            title="Development Video"
            width="100%"
            height="298px"
            src="https://www.youtube.com/embed/Z2LrckBa9oU?rel=0&modestbranding=1&autohide=1&showinfo=0"
            frameBorder="0"
            allowFullScreen
          />
          <Text {...textProps}>
            <strong>ABOUT YOUR NEW HOME</strong>
          </Text>
          <Text {...textProps}>
            With convenient access to local schools, leisure facilities, shopping and even a hospital on the door step,
            Drumford Meadow really is an ideal place to live. Access to the M1 and A3 is only a few minutes away or go
            green with immediate access to a network of cycle paths, playing fields and walk ways. Portadown itself can
            boast as home to some of N.Irelands leading employers and has a thriving town centre with a mix of shops,
            cafes, bars and restaurants to tickle all taste buds.&nbsp;
          </Text>
          <Text {...textProps}>
            <strong>The Main Structure:</strong>
          </Text>
          <Text {...textProps}>
            Walls are of traditional, tried and tested cavity construction. Brick and/or plaster render are used for the
            external finish topped by a striking black coloured uPVC square edge fascia, barge and soffit. External
            timber features are painted gloss black to match where applicable. The roof will have a grey coloured, flat
            concrete tile crowned by a dry fixed ridge tile. Black coloured uPVC hexagonal guttering with square uPVC
            downpipes compliment the external trim.
          </Text>
          <Text {...textProps}>
            Energy saving white uPVC double glazed windows with locking latches (except those deemed emergency escape
            routes) are standard. White coloured uPVC French doors or aluminium sliding patio doors are fitted where
            applicable. A low maintenance GRP front door and uPVC rear door with double-glazing complete the external
            elevation.
          </Text>
          <Text {...textProps}>
            <strong>External:</strong>
          </Text>
          <Text {...textProps}>
            Your home will look complete from day 1 - all garden areas will have a front and rear lawn unless otherwise
            specified or shown on the site layout. Flat top garden edge and treated rail fencing will be erected where
            applicable. Fencing will include a timber privacy screen between adjoining properties; all other&nbsp;
            fenced areas will comprise of treated timber posts and rail (approx 900mm high).
          </Text>
          <Text {...textProps}>
            Driveways will be approx 10 metres long or from the road kerb to the front elevation of the property,
            whichever is the greater, and finished in bitmac. Garages (where applicable) are counted as a parking space
            and may reduce the length of driveways. A coloured pavia brick path will be provided to at least the front
            and rear elevation with level access to one external door. Pavia patios will have a minimum area of 5.75m2
            where applicable. Specified plots may have a facing brick boundary wall subject to planning conditions on
            site. You can even wash your car and water the plants using the outside tap which is provided as standard!
          </Text>
          <Text {...textProps}>
            <strong>Internal:&nbsp;</strong>
          </Text>
          <Text {...textProps}>
            5” contemporary MDF skirting and 3” contemporary MDF architrave painted gloss white is the perfect trim for
            each room. Painted MDF cillboard nosed and rebated provide a seamless finish to the windows. This is all
            complimented by moulded &apos;ladder&apos; style internal doors painted white, with brushed chrome coloured
            latch furniture. Redwood newel posts and handrails with square balustrades treated with clear preservative
            where appropriate are incorporated in homes with stairways.&nbsp;
          </Text>
          <Text {...textProps}>
            There will be smooth plastered ceilings and a white, insulated, uPVC loft access trap door will be fitted in
            the relevant area. Ventilation/extractor fans are fitted in the kitchen, bathroom, ensuite (where
            applicable) and WC.
          </Text>
          <Text {...textProps}>
            <strong>Heating:&nbsp;</strong>
          </Text>
          <Text {...textProps}>
            Energy conservation and running costs are important to all of us so thermal insulation to walls, roof space
            and habitable areas is provided to achieve a substantial energy saving. A specially designed mains gas
            central heating system is standard in each home. This will incorporate a boiler and there will be radiators
            in all main rooms. The system is programme controlled to ensure a performance suitable for most individual
            requirements and to provide you with a separate hot water supply. Your new home will have a pressurised
            water system.
          </Text>
          <Text {...textProps}>
            <strong>For your Peace of Mind:&nbsp;</strong>
          </Text>
          <Text {...textProps}>
            An intruder alarm is supplied and installed by a NACOSS &amp; NSI approved company. Front and back doors
            have multi lock systems and all ground floor windows are lockable. Electrically operated smoke and carbon
            monoxide detectors are installed. For your convenience, external lights are fitted at both the front and
            back door. The front door will have a door bell. Every property is registered with the National House
            Building Council (NHBC) 10 Year warranty and insurance scheme known as Buildmark. As such we also adopt the
            requirements of the Consumer Code for Home Builders.
          </Text>
          <Text {...textProps}>
            <strong>
              Did you know you could personalise your new home even before you move in? You could choose tiles,
              flooring, kitchens, power socket positions and many more features besides. We aim to provide you with an
              opportunity to get creative and put your stamp on a truly blank canvas. We build many choices and options
              in as we construct your new home, which means you&apos;ll have access to a wider range, the earlier you
              reserve. Our team will advise you on the options available on each property when you reserve a new home.
            </strong>
          </Text>
          <DevUnorderdList {...textProps}>
            <li>
              We install a quality, practical kitchen (choice of door, worktop and handle from a range supplied by ACC
              Ltd).&nbsp;
            </li>
            <li>
              No kitchen is complete without appliances so we supply and fit a 4 ring gas hob and single electric oven
              complete with stylish extractor canopy, 70/30 integrated fridge freezer, dishwasher, washing machine and
              tumble dryer. In order to maximise cupboard space, some properties will be fitted with a modern combi
              washer/dryer where applicable.
            </li>
            <li>
              Your bathroom, wc/cloaks and ensuite (where applicable) will have contemporary white sanitary ware with
              chrome finished fittings. This includes a chrome finished towel radiator in the main bathroom and ensuite
              (where applicable). Shower cubicles with glass screens will be fitted in the bathroom and ensuite (where
              applicable). Where a property does not have space for a separate shower cubicle in the main bathroom; we
              will install a shower over the bath complete with glass screen. The first / main shower will operate using
              the property&apos;s heating system. Additional showers (where applicable) will be electrically operated.
            </li>
            <li>
              Tailor your colour scheme from our extensive choice of quality floor tiles for halls, kitchen, utility,
              bathroom and ensuite/WC/cloak areas. Where properties have an internal porch plus a hall, the porch will
              be tiled and the hall will have carpet (see sales staff for details applicable to each property type).
            </li>
            <li>
              There is also a similar choice of feature wall tiles for the kitchen, bathroom and ensuite/WC/cloaks
              splash back areas (see Sales Staff for areas and details relevant to your particular property type).&nbsp;
            </li>
            <li>
              A choice of carpet with quality underlay is provided for the lounge, stairs / landing and all
              bedrooms.&nbsp;
            </li>
            <li>
              There will be a wall mounted, glass screen, electric, dancing flame effect fire in the lounge. This will
              have both heat and/or decorative flame options.&nbsp;
            </li>
            <li>
              A generous provision of power points is provided throughout the house and where possible, they will be
              positioned to suit individual needs. This includes feature down lighting in at least the lounge and
              kitchen. TV points are provided in all bedrooms and a telephone point is installed beside the lounge TV
              point and in the smallest bedroom/study.&nbsp;
            </li>
            <li>
              And for the finishing touch ceilings will be painted white and the internal walls will be painted one
              neutral colour throughout.
            </li>
          </DevUnorderdList>
          <Text {...textProps}>
            Additional options may be considered but we can only incorporate them into the property if a binding
            contract is in existence between all&nbsp;
          </Text>
          <Text {...textProps}>
            <strong>Finally, keeping it all in shape</strong>
          </Text>
          <Text {...textProps}>
            Landscaped areas have been incorporated to compliment your new home and these areas will require continuing
            management in the future in order to maintain the quality environment in which you live. For this purpose,
            Antrim Construction Company Limited has entered into an agreement with a management company. Residents will
            contribute to this service. The first payment will be made to Antrim Construction Company Limited at the
            same time as your balance purchase money is paid.
          </Text>
          <Text {...textProps}>
            <em>
              This specification is for guidance only and may be subject to variation. Although every care has been
              taken to ensure&nbsp;
            </em>
            <em>
              the accuracy of all information given, these contents do not form part of or constitute a representation,
              warranty, or part&nbsp;
            </em>
            <em>of, any contract. The right is reserved to alter or amend any details.</em>
          </Text>
        </DevelopmentSection>
      </DevelopmentContent>
    </div>
  );
};

export default DrumfordMeadowContent;
